/**
 * zoom-control.js — row 2's right anchor, second half.
 *
 * The zoom chip and its popover. Owns the number the USER chose; the fit is
 * somebody else's (`ViewportSwitcher`), and the iframe is sent the product.
 *
 * ── One number became two (Mobile P20), and it stays two ──────────────────
 *   `_userZoom`  — what the user picked. The only thing persisted.
 *   `fitScale`   — derived from the space available. Never written anywhere.
 *   sent to the iframe: `clampZoom(userZoom × fitScale)`
 *
 * The label reports the USER's number, not the product: the fit is a
 * consequence of the window, and showing "51%" for a value nobody picked reads
 * as a bug.
 *
 * ── The zoom trap this closes ─────────────────────────────────────────────
 * The chip is `display: none` on a phone, a persisted desktop value was still
 * applied at boot, and neither escape (Ctrl/Cmd+0, the command palette) exists
 * there. A phone boot resets the user zoom and does NOT write it back, so
 * returning to a desktop restores the desktop value — it was never overwritten.
 *
 * ── Migrated to the kit (CMD-5) ───────────────────────────────────────────
 * Every button here came from `createButton` and every glyph from `icon()`.
 * The chip, the two steppers and the reset button were hand-rolled `<button>`s
 * with attribute-sized inline SVGs; between this file and `viewport-switcher.js`
 * they are the last of the four sweep allowlist entries `ui/viewport-controls.js`
 * held.
 */

import { bridge } from '../../editing/bridge.js';
import { eventBus } from '../../core/events.js';
import { createButton } from '../components.js';
import { icon } from '../icons.js';
import { isPhoneViewport } from '../../shared/viewport-service.js';
import {
  ZOOM_STEPS,
  ZOOM_DEFAULT,
  clampZoom,
  readPersistedZoom,
  writePersistedZoom
} from '../viewport-controls.js';

function zoomStorage() {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null;
  } catch {
    // Expected flow: storage is unavailable in private modes and sandboxed
    // frames. Zoom then simply does not persist, which is the correct
    // degradation for a view preference.
    return null;
  }
}

export class ZoomControl {
  /**
   * @param {HTMLElement} container
   * @param {{getFitScale?: () => number}} [opts] — the seam to `ViewportSwitcher`.
   */
  constructor(container, opts = {}) {
    this._container = container;
    this._getFitScale = opts.getFitScale || (() => 1);

    const persisted = readPersistedZoom(zoomStorage()) ?? ZOOM_DEFAULT;
    this._userZoom = isPhoneViewport() ? ZOOM_DEFAULT : persisted;
    this._open = false;
    this._unsubs = [];
    this._render();
    // Reflect a persisted (non-default) zoom in the chip on boot. The
    // bridge-ready handler applies it to the preview once the iframe exists.
    this._updateLabel();
    this._listen();
  }

  destroy() {
    for (const unsub of this._unsubs) unsub?.();
    this._unsubs = [];
    document.removeEventListener('click', this._onOutsideClick);
    document.removeEventListener('keydown', this._onDocumentKeydown);
  }

  get userZoom() {
    return this._userZoom;
  }

  /** What the iframe is actually told: the user's choice through the fit. */
  effectiveZoom() {
    return clampZoom(this._userZoom * this._getFitScale());
  }

  _render() {
    const wrap = document.createElement('div');
    wrap.className = 'pb-zoom-chip-wrap';

    this._valueEl = document.createElement('span');
    this._valueEl.className = 'pb-zoom-chip-value';

    this._chip = createButton({
      variant: 'bare',
      className: 'pb-zoom-chip',
      icon: icon('search', { size: 'sm' }),
      content: this._valueEl,
      ariaLabel: 'Zoom controls',
      tooltip: 'Zoom controls',
      tooltipShortcut: 'Ctrl+0',
      ariaHaspopup: 'menu',
      ariaExpanded: 'false',
      onClick: (e) => {
        e.stopPropagation();
        this._open ? this.close() : this.openMenu();
      }
    });

    this._menu = document.createElement('div');
    this._menu.className = 'pb-zoom-popover';
    this._menu.setAttribute('role', 'menu');

    const row = document.createElement('div');
    row.className = 'pb-zoom-popover-row';
    row.setAttribute('role', 'none');

    this._resetBtn = createButton({
      variant: 'bare',
      className: 'pb-zoom-popover-reset',
      role: 'menuitem',
      label: '100%',
      ariaLabel: 'Reset zoom to 100%',
      onClick: () => this.zoomReset()
    });

    row.append(
      createButton({
        variant: 'bare',
        className: 'pb-zoom-popover-btn pb-cb-icon',
        role: 'menuitem',
        icon: icon('minus', { size: 'sm' }),
        ariaLabel: 'Zoom out',
        tooltip: 'Zoom out',
        tooltipShortcut: 'Ctrl+-',
        onClick: () => this.zoomOut()
      }),
      this._resetBtn,
      createButton({
        variant: 'bare',
        className: 'pb-zoom-popover-btn pb-cb-icon',
        role: 'menuitem',
        icon: icon('plus', { size: 'sm' }),
        ariaLabel: 'Zoom in',
        tooltip: 'Zoom in',
        tooltipShortcut: 'Ctrl+=',
        onClick: () => this.zoomIn()
      })
    );

    const label = document.createElement('label');
    label.className = 'pb-zoom-slider-label';
    label.setAttribute('role', 'none');
    const caption = document.createElement('span');
    caption.textContent = 'Zoom';
    this._slider = document.createElement('input');
    this._slider.className = 'pb-zoom-slider';
    this._slider.type = 'range';
    this._slider.min = '25';
    this._slider.max = '150';
    this._slider.step = '1';
    this._slider.value = '100';
    this._slider.setAttribute('aria-label', 'Zoom level');
    this._slider.addEventListener('input', () => {
      this._setZoom(Number(this._slider.value) / 100);
    });
    label.append(caption, this._slider);

    this._menu.append(row, label);
    this._menu.addEventListener('keydown', (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      this.close();
      this._chip.focus();
    });

    wrap.append(this._chip, this._menu);
    this._container.appendChild(wrap);

    this._onOutsideClick = (e) => {
      if (!this._open) return;
      if (wrap.contains(e.target)) return;
      this.close();
    };
    this._onDocumentKeydown = (e) => {
      if (e.key === 'Escape' && this._open) this.close();
    };
    document.addEventListener('click', this._onOutsideClick);
    document.addEventListener('keydown', this._onDocumentKeydown);
  }

  _listen() {
    this._unsubs.push(
      eventBus.on('iframe:bridge-ready', () => {
        // The switcher has already derived the fit by the time the bridge is
        // ready; asking for it here rather than caching it is what keeps the
        // two halves from disagreeing about which number is current.
        if (this.effectiveZoom() !== ZOOM_DEFAULT) {
          bridge.send('set-zoom', { zoom: this.effectiveZoom() });
        }
      })
    );
    this._unsubs.push(eventBus.on('toolbar:zoom-in', () => this.zoomIn()));
    this._unsubs.push(eventBus.on('toolbar:zoom-out', () => this.zoomOut()));
    this._unsubs.push(eventBus.on('toolbar:zoom-reset', () => this.zoomReset()));
  }

  zoomIn() {
    const next = ZOOM_STEPS.find((z) => z > this._userZoom);
    if (next) this._setZoom(next);
  }

  zoomOut() {
    const prev = [...ZOOM_STEPS].reverse().find((z) => z < this._userZoom);
    if (prev) this._setZoom(prev);
  }

  zoomReset() {
    this._setZoom(ZOOM_DEFAULT);
  }

  _setZoom(level) {
    this._userZoom = clampZoom(level);
    // Never persist from a phone. The chip is hidden there and both escapes are
    // unreachable, so a value written here could not be undone from the device
    // that wrote it — and it would follow the user back to their desktop.
    if (!isPhoneViewport()) writePersistedZoom(zoomStorage(), this._userZoom);
    this.push();
  }

  /** Send the product. Called by `ViewportSwitcher` when the fit moves, too. */
  push() {
    const effective = this.effectiveZoom();
    bridge.send('set-zoom', { zoom: effective === ZOOM_DEFAULT ? null : effective });
    this._updateLabel();
    setTimeout(() => bridge.send('query-section-positions', {}), 50);
  }

  _updateLabel() {
    const pct = Math.round(this._userZoom * 100);
    if (this._valueEl) this._valueEl.textContent = `${pct}%`;
    if (this._slider) this._slider.value = String(pct);
    this._chip?.classList.toggle('pb-zoom-default', this._userZoom === ZOOM_DEFAULT);
  }

  openMenu() {
    this._open = true;
    this._menu.classList.add('pb-open');
    this._chip.setAttribute('aria-expanded', 'true');
  }

  close() {
    this._open = false;
    this._menu.classList.remove('pb-open');
    this._chip.setAttribute('aria-expanded', 'false');
  }
}
